import { Component, EventEmitter, Input, Output, OnInit, OnDestroy, ChangeDetectionStrategy } from '@angular/core';
import { NgForOf, NgIf } from '@angular/common';
import { RouterLink } from '@angular/router';
import { FormsModule } from '@angular/forms';
import { DatePickerModule } from '@openng/optimus-ui/datepicker';
import { DividerModule } from '@openng/optimus-ui/divider';
import { ICampaignData, ICampaignPhase, IChannelItem } from '../../../models/campaign/campaign.interface';
import { TypeEnum } from '../../../models/template/type.enum';
import { ChannelConfig } from './channel-config';
import { AutoCompleteModule, AutoCompleteSelectEvent } from '@openng/optimus-ui/autocomplete';
import { SearchTemplateService } from '../../../services/search-template.service';
import { Subject, takeUntil } from 'rxjs';
import { CampaignService } from '../../../services/campaign.service';
import { ToastBarService } from '../../../services/toast-bar.service';
import { HttpErrorResponse } from '@angular/common/http';
import { Template } from '../../../models/template/template.interface';

interface AutoCompleteCompleteEvent {
  originalEvent: Event;
  query: string;
}

@Component({
    selector: 'app-channel-details',
    imports: [
        NgIf,
        NgForOf,
        RouterLink,
        FormsModule,
        DatePickerModule,
        DividerModule,
        AutoCompleteModule
    ],
    templateUrl: './channel-details.component.html',
    changeDetection: ChangeDetectionStrategy.Eager,
    styleUrl: './channel-details.component.scss'
})
export class ChannelDetailsComponent implements OnDestroy, OnInit {

  @Input({required: true}) config!: ChannelConfig;
  @Input({required: true}) phase!: ICampaignPhase;
  @Input({required: true}) campaignData!: ICampaignData;
  @Input() channels: IChannelItem[] = [];
  @Input() nextChannelNumber: number = 1;

  @Output() removeTemplate = new EventEmitter<{ phase: ICampaignPhase; channelId: string; type: TypeEnum }>();
  @Output() plannedDateChange = new EventEmitter<{ channel: IChannelItem; event: any }>();
  @Output() templateConnected = new EventEmitter<{ phase: ICampaignPhase; type: TypeEnum; template: Template }>();

  constructor(private searchTemplateService: SearchTemplateService, private campaignService: CampaignService, private toastbarService: ToastBarService) {
  }

  ngOnInit(): void {
    this.searchTemplateService.searchAtTemplate$
      .pipe(takeUntil(this._destroy$))
      .subscribe({
        next: (res: string[]) => {
          // Populate the suggestions array
          this.suggestions = res;
        }
      })
  }

  ngOnDestroy(): void {
    // Used for unsubscribing
    this._destroy$.next();
    this._destroy$.complete();
  }

  templates: any[] | undefined;

  selectedTemplate: any;

  suggestions!: any[];

  _destroy$ = new Subject<void>();

  searchTemplate(event: AutoCompleteCompleteEvent) {
    this.searchTemplateService.checkSearchDataAtEditor(event.query, this.config.typeEnum);
  }

  connectTemplateToPhase(event: AutoCompleteSelectEvent) {
    // Check if template already is in the phase channel
    if (this.isChannelAlreadyConnected(event)) {
      return;
    }

    this.campaignService.connectTemplateToPhase({
      phaseId: this.phase.phaseId!,
      campaignId: this.campaignData.campaignId,
      templateType: this.config.typeEnum,
      templateName: event.value
    })
      .pipe(takeUntil(this._destroy$))
      .subscribe({
        next: (template: Template) => {
          this.toastbarService.showToastSuccess("Template succesfully connected to phase!");
          this.templateConnected.emit({phase: this.phase, type: this.config.typeEnum, template});
          this.selectedTemplate = null;
        },
        error: (err: HttpErrorResponse) => {
          console.error("Something went wrong when connecting the template to phase!", err);
          this.toastbarService.showToastError("Something went wrong when connecting the template to phase!");
        }
      })
  }

  private isChannelAlreadyConnected(event: AutoCompleteSelectEvent) {
    const existingChannels = (this.phase.channels as any)?.[this.config.phaseSuffix] as (IChannelItem | string)[] | undefined;
    const alreadyConnected = existingChannels?.some(channel => (typeof channel === 'string' ? channel : channel?.name) === event.value
    );
    if (alreadyConnected) {
      this.toastbarService.showToastError("This template is already connected to the phase!");
      this.selectedTemplate = null;
      return true;
    } else {
      return false;
    }
  }

  parseDateFromName(templateName: string, returnWithSlashes: boolean = false): string {
    if (!templateName) return 'N/A';

    const FORMAT_REGEX = /^\d{8}_/;
    if (!FORMAT_REGEX.test(templateName)) return 'N/A';

    const datePart = templateName.slice(0, 8);
    const year = datePart.slice(0, 4);
    const month = datePart.slice(4, 6);
    const day = datePart.slice(6, 8);

    const y = Number(year);
    const m = Number(month);
    const d = Number(day);
    const date = new Date(y, m - 1, d);
    const isValidDate =
      date.getFullYear() === y &&
      date.getMonth() === m - 1 &&
      date.getDate() === d;

    if (!isValidDate) return 'N/A';

    return returnWithSlashes
      ? `${day}/${month}/${year}`
      : `${day}.${month}.${year}`;
  }
}
